<template>
  <div class="screen-root">
    <div class="screen" ref="screen">
      <!-- ============ 顶部标题栏 ============ -->
      <header class="hdr">
        <div class="hdr-left">
          <div class="brand">
            <i class="el-icon-s-platform brand-ic"></i>
            <b>统建统服</b>
          </div>
          <span class="hdr-en">CHARGING OPERATION CENTER</span>
        </div>

        <h1 class="hdr-title">运营商数据大屏</h1>
        <span class="hdr-sub">TONGJIAN TONGFU · OPERATION DATA VISUALIZATION</span>
        <span class="hdr-wing l"></span>
        <span class="hdr-wing r"></span>

        <div class="hdr-right">
          <span class="hdr-btn" title="刷新数据" @click="refreshData">
            <i class="el-icon-refresh" :class="{ spin: loading }"></i>
          </span>
          <span class="hdr-btn" :title="isFullscreen ? '退出全屏' : '全屏显示'" @click="toggleFullscreen">
            <i :class="isFullscreen ? 'el-icon-close' : 'el-icon-full-screen'"></i>
          </span>
          <div class="hdr-chip">
            <span class="live-dot"></span>
            <span class="chip-txt">数据实时同步</span>
          </div>
          <div class="hdr-chip">
            <i class="el-icon-date chip-ic"></i>
            <span class="date-val">{{ dateText }}</span>
          </div>
          <div class="hdr-chip">
            <i class="el-icon-time chip-ic"></i>
            <span class="time-val mono">{{ timeText }}</span>
          </div>
        </div>
      </header>

      <!-- ============ 主体 ============ -->
      <div class="main">
        <!-- ---------- 设备总览（通栏） ---------- -->
        <section class="panel dev-panel">
          <div class="panel-hd">
            <span class="bar"></span>
            <i class="el-icon-cpu ic"></i>
            <h3>设备总览</h3>
            <div class="extra">
              <span class="chart-unit">
                设备总数<b class="mono dv-hd-num">{{ formatInteger(deviceTotal) }}</b>台
              </span>
              <span class="dv-hd-split"></span>
              <span class="chart-unit">
                在线设备<b class="mono dv-hd-num">{{ formatInteger(device.online) }}</b>台
              </span>
              <span class="dv-hd-split"></span>
              <span class="chart-unit">
                在线率<b class="mono dv-hd-num">{{ deviceOnlineRate }}%</b>
              </span>
            </div>
          </div>

          <div class="panel-bd">
            <div class="dev-cards">
              <div class="dev-card" v-for="item in deviceList" :key="item.key" :class="item.key">
                <span class="dv-ic"><i :class="item.icon"></i></span>
                <div class="dv-main">
                  <div class="dv-label">{{ item.label }}</div>
                  <div class="dv-val">
                    <b class="mono">{{ formatInteger(item.value) }}</b>
                    <small>台</small>
                  </div>
                </div>
                <div class="dv-side">
                  <span class="dv-pct mono">{{ item.pct }}%</span>
                  <span class="dv-bar"><i :style="{ width: item.pct + '%' }"></i></span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- ---------- 左列：运营数据 ---------- -->
        <div class="col col-l">
          <section class="panel" style="flex: 1;">
            <div class="panel-hd">
              <span class="bar"></span>
              <i class="el-icon-data-line ic"></i>
              <h3>运营数据</h3>
              <div class="extra">
                <span class="chart-unit">本月</span>
                <div class="select" :class="{ open: openSelect === 'stat' }" @click.stop>
                  <button class="select-btn" @click="toggleSelect('stat')">
                    <span>{{ statRange }}</span>
                    <i class="el-icon-arrow-down caret"></i>
                  </button>
                  <transition name="drop">
                    <ul class="select-list" v-show="openSelect === 'stat'">
                      <li
                        v-for="opt in rangeOptions"
                        :key="opt"
                        :class="{ active: opt === statRange }"
                        @click="pickSelect('stat', opt)"
                      >{{ opt }}</li>
                    </ul>
                  </transition>
                </div>
              </div>
            </div>

            <div class="panel-bd">
              <div class="stat-list">
                <div class="stat-row" v-for="item in statList" :key="item.key">
                  <span class="sr-ic" :style="{ background: item.bg, color: item.color }">
                    <i :class="item.icon"></i>
                  </span>

                  <div class="sr-main">
                    <div class="sr-label">{{ item.label }}</div>
                    <div class="sr-val">
                      <span class="num mono">{{ item.value }}</span>
                      <span class="unit" v-if="item.unit">{{ item.unit }}</span>
                    </div>
                  </div>

                  <div class="sr-side">
                    <template v-if="item.isNew">
                      <span class="sc-foot tag-new">
                        <i class="el-icon-circle-plus-outline"></i>
                        <span>新增</span>
                      </span>
                    </template>

                    <template v-else-if="item.trend">
                      <div class="sc-foot" :class="item.dir">
                        <i :class="item.dir === 'up' ? 'el-icon-top' : 'el-icon-bottom'"></i>
                        <span class="mono">{{ Math.abs(item.trend) }}%</span>
                      </div>
                      <div class="sr-tip">环比</div>
                    </template>

                    <template v-else>
                      <div class="sr-tip">{{ item.note }}</div>
                    </template>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>

        <!-- ---------- 中列 ---------- -->
        <div class="col col-c">
          <!-- 用电月度统计 -->
          <section class="panel" style="flex: 1.15 1 0;">
            <div class="panel-hd">
              <span class="bar"></span>
              <i class="el-icon-data-analysis ic"></i>
              <h3>用电月度统计</h3>
              <div class="extra">
                <span class="chart-unit">单位：kWh</span>
                <div class="chart-legend"><i></i><span>用电量</span></div>
              </div>
            </div>
            <div class="panel-bd">
              <div class="chart" ref="monthChart"></div>
              <div class="foot-note">
                <i class="el-icon-info"></i>
                <span>统计口径：按自然月汇总各站点计量点的总用电量，当月数据实时累计</span>
              </div>
            </div>
          </section>

          <!-- 功率曲线 -->
          <section class="panel" style="flex: 1 1 0;">
            <div class="panel-hd">
              <span class="bar"></span>
              <i class="el-icon-s-platform ic"></i>
              <h3>功率曲线</h3>
              <div class="extra">
                <span class="chart-unit">调控概览 · 今日</span>
              </div>
            </div>
            <div class="panel-bd">
              <div class="ctrl-row">
                <span class="k">
                  <i class="el-icon-switch-button"></i>
                  当日调控次数
                </span>
                <span class="v mono">{{ power.ctrlCount }} <small>次</small></span>
              </div>
              <div class="ctrl-row">
                <span class="k">
                  <i class="el-icon-lightning"></i>
                  当日调控功率
                </span>
                <span class="v mono">{{ power.ctrlPower }} <small>kW</small></span>
              </div>

              <div class="sub-line">
                <span>24 小时功率曲线</span>
                <span class="cur">当前 <b class="mono">{{ power.current }}</b> <span class="cur-unit">kW</span></span>
              </div>

              <div class="chart" ref="powerChart"></div>
            </div>
          </section>
        </div>

        <!-- ---------- 右列 ---------- -->
        <div class="col col-r">
          <!-- 站点用电排行 -->
          <section class="panel" style="flex: 1.1 1 0;">
            <div class="panel-hd">
              <span class="bar"></span>
              <i class="el-icon-trophy ic"></i>
              <h3>站点用电排行</h3>
              <div class="extra">
                <span class="chart-unit">Top {{ rankList.length }}</span>
                <div class="select" :class="{ open: openSelect === 'rank' }" @click.stop>
                  <button class="select-btn" @click="toggleSelect('rank')">
                    <span>{{ rankRange }}</span>
                    <i class="el-icon-arrow-down caret"></i>
                  </button>
                  <transition name="drop">
                    <ul class="select-list" v-show="openSelect === 'rank'">
                      <li
                        v-for="opt in rangeOptions"
                        :key="opt"
                        :class="{ active: opt === rankRange }"
                        @click="pickSelect('rank', opt)"
                      >{{ opt }}</li>
                    </ul>
                  </transition>
                </div>
              </div>
            </div>

            <div class="panel-bd">
              <div class="rank-table">
                <div class="rt-head">
                  <span class="c-rank">#</span>
                  <span class="c-name">站点名称</span>
                  <span class="c-kwh">用电量(kWh)</span>
                  <span class="c-fee">收入(¥)</span>
                  <span class="c-ord">订单数</span>
                  <span class="c-tim">用电时间(h)</span>
                </div>
                <div class="rt-body">
                  <div class="rt-row" v-for="(row, idx) in rankList" :key="row.id + '_' + idx">
                    <span class="c-rank">
                      <span class="rk" :class="'top' + (idx + 1)" v-if="idx < 3">{{ idx + 1 }}</span>
                      <span class="rk" v-else>{{ idx + 1 }}</span>
                    </span>
                    <span class="c-name">
                      <div class="rn-1">{{ row.name }}</div>
                      <div class="rn-2">
                        <i class="el-icon-office-building"></i>
                        <span>NO.{{ row.id }}</span>
                      </div>
                    </span>
                    <span class="c-kwh v-kwh mono">{{ row.kwh }}</span>
                    <span class="c-fee v-fee mono">{{ row.fee }}</span>
                    <span class="c-ord v-ord mono">{{ row.order }}</span>
                    <span class="c-tim v-tim mono">{{ row.time }}</span>
                  </div>
                  <div class="rt-empty" v-if="rankList.length === 0">暂无数据</div>
                </div>
              </div>
            </div>
          </section>

          <!-- 安全监测 -->
          <section class="panel" style="flex: 1.06 1 0;">
            <div class="panel-hd">
              <span class="bar"></span>
              <i class="el-icon-first-aid-kit ic"></i>
              <h3>安全监测</h3>
              <div class="extra">
                <span class="safe-rate" :class="safeRateLevel">
                  <i class="el-icon-warning-outline"></i>
                  <span class="srate-txt">异常率</span>
                  <b class="mono">{{ safeRate }}{{ safeRateUnit }}</b>
                </span>
              </div>
            </div>

            <div class="panel-bd">
              <div class="safe-top">
                <div class="safe-box normal">
                  <div class="sb-num mono" :class="{ 'sb-num-sm': formatInteger(safe.normal).length > 7 }">{{ formatInteger(safe.normal) }}</div>
                  <div class="sb-lb">正常次数</div>
                </div>
                <div class="safe-box warn">
                  <div class="sb-num mono">{{ formatInteger(safe.warn) }}</div>
                  <div class="sb-lb">预警次数</div>
                </div>
                <div class="safe-box alarm">
                  <div class="sb-num mono">{{ formatInteger(safe.alarm) }}</div>
                  <div class="sb-lb">报警次数</div>
                </div>
              </div>

              <div
                class="safe-list"
                ref="safeList"
                v-if="safe.list.length > 0"
                @mouseenter="safePaused = true"
                @mouseleave="safePaused = false"
              >
                <div
                  class="safe-track"
                  :class="{ run: safeScroll, paused: safePaused }"
                  :style="{ animationDuration: safeDuration + 's' }"
                >
                  <div
                    class="safe-item"
                    v-for="(item, i) in safeRenderList"
                    :key="i"
                    :class="'lv' + item.level"
                  >
                    <div class="si-top">
                      <span class="si-time mono">
                        <i class="el-icon-time"></i>
                        <span>{{ item.time }}</span>
                      </span>
                      <span class="si-name" :style="{ color: item.color }" :title="item.type">{{ item.type }}</span>
                    </div>
                    <div class="si-addr" :title="item.address">
                      <i class="el-icon-location-outline"></i>
                      <span>{{ item.address }}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="rt-empty" v-else>暂无数据</div>
            </div>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from "echarts"
import screenfull from "screenfull"
import {
  getHomeCurMonthBusiDataApi,
  getHomeUseElectChartDataApi,
  getHomeUseElectSiteYxPhDataApi,
  getHomeStrateDataApi,
  getHomeDeviceOverviewDataApi,
  getHomeSafeMonitorDataApi
} from "@/api/data_statistic"
import { SAFETY_MONITOR_MOCK, formatAbnormalRate } from "@/utils/safety-monitor"

/* ================= 图表配色 ================= */
const C = {
  cyan: "#00e5ff",
  sky: "#38c6ff",
  blue: "#2f8cff",
  grid: "rgba(120, 180, 240, .10)",
  axis: "rgba(120, 180, 240, .26)",
  label: "#8fa9c4"
}

const TOOLTIP = {
  trigger: "axis",
  backgroundColor: "rgba(6, 24, 44, .94)",
  borderColor: "rgba(0, 190, 255, .45)",
  borderWidth: 1,
  padding: [8, 12],
  textStyle: { color: "#dff1ff", fontSize: 12 },
  axisPointer: {
    type: "line",
    lineStyle: { color: "rgba(0, 220, 255, .55)", type: "dashed" }
  }
}

/* ================= 设备总览示例数据 =================
 * 设备总览接口（data_statistic.js 中的路径为示例假路径）暂未提供，
 * 先用示例数据渲染界面；后端接口就绪后把 data 中的 deviceMock 置为 false 即可切到真实数据
 */
const DEVICE_MOCK = {
  online_num: 1286,
  offline_num: 74,
  charging_num: 428,
  idle_num: 835,
  fault_num: 23
}

/* ================= 安全监测示例数据 =================
 * 安全监测接口（data_statistic.js 中为示例假路径）暂未提供，先用示例数据渲染，
 * 示例数据与安全监测列表页共用（@/utils/safety-monitor），保证两侧统计与明细一致；
 * 接口就绪后把 data 中的 safeMock 置为 false 即可切到真实数据
 */

/* 异常类型名称配色：不同类型异常名称字体颜色不同 */
const SAFE_TYPE_COLORS = {
  过温告警: "#ff6b7d",
  过流保护: "#ff7ab6",
  漏电保护: "#ffc53d",
  急停触发: "#8b7bff",
  绝缘故障: "#2f8cff",
  直流母线绝缘异常: "#2f8cff",
  通信中断: "#00e5ff",
  枪锁异常: "#38c6ff",
  计量异常: "#25e0a8",
  过压告警: "#ffc53d",
  欠压告警: "#38c6ff"
}
const SAFE_TYPE_PALETTE = ["#ff6b7d", "#ffc53d", "#38c6ff", "#8b7bff", "#00e5ff", "#ff7ab6", "#2f8cff", "#25e0a8"]

/* 列表单条外间距，需与 .safe-item 的 margin-bottom 保持一致（用于计算自动滚动） */
const SAFE_ITEM_GAP = 8

export default {
  name: "DataScreen",

  data() {
    return {
      now: new Date(),
      timer: null,
      dataTimer: null,
      loading: false,
      isFullscreen: false,

      openSelect: "",

      rangeOptions: ["今日", "近 7 日", "本月", "本季度"],
      statRange: "本月",
      rankRange: "本月",

      // —— 板块 0：设备总览 ——
      deviceMock: true,
      device: { online: 0, offline: 0, charging: 0, idle: 0, fault: 0 },

      // —— 板块 1：运营数据 ——
      statList: [
        { key: "kwh", label: "本月用电量", value: "*.**", unit: "kWh", icon: "el-icon-lightning", color: "#2f8cff", bg: "rgba(47,140,255,.15)", trend: null, dir: "up" },
        { key: "fee", label: "本月总电费", value: "¥*.**", unit: "", icon: "el-icon-money", color: "#25e0a8", bg: "rgba(37,224,168,.15)", trend: null, dir: "up" },
        { key: "svc", label: "本月总服务费", value: "¥*.**", unit: "", icon: "el-icon-tickets", color: "#ffc53d", bg: "rgba(255,197,61,.15)", trend: null, dir: "up" },
        { key: "ord", label: "本月总订单数", value: "***", unit: "单", icon: "el-icon-shopping-cart-2", color: "#8b7bff", bg: "rgba(139,123,255,.15)", trend: null, dir: "up" },
        { key: "tim", label: "本月用电时间", value: "*.**", unit: "小时", icon: "el-icon-odometer", color: "#ff7ab6", bg: "rgba(255,122,182,.15)", trend: null, dir: "up" },
        { key: "new", label: "本月开户数", value: "***", unit: "户", icon: "el-icon-mobile", color: "#25e0a8", bg: "rgba(37,224,168,.15)", isNew: false, trend: null, dir: "up" },
        { key: "tot", label: "总开户数", value: "***", unit: "户", icon: "el-icon-user", color: "#8b7bff", bg: "rgba(139,123,255,.15)", note: "累计已开户" },
        { key: "left", label: "剩余开户数", value: "***", unit: "户", icon: "el-icon-refrigerator", color: "#ffc53d", bg: "rgba(255,197,61,.15)", note: "可开户额度" }
      ],

      // —— 板块 3：站点用电排行 ——
      rankList: [],

      // —— 板块 4：安全监测 ——
      safeMock: true,
      safe: { normal: 0, warn: 0, alarm: 0, list: [] },
      safePaused: false,
      safeScroll: false,
      safeDuration: 20,

      // —— 板块 5：功率曲线 ——
      power: {
        ctrlCount: 0,
        ctrlPower: "0.00",
        current: "0.00",
        hours: ["00", "01", "02", "03", "04", "05", "06", "07", "08", "09", "10", "11", "12", "13", "14", "15", "16", "17", "18", "19", "20", "21", "22", "23"],
        values: []
      },

      // —— 板块 2：用电月度统计 ——
      month: { labels: [], values: [] },

      monthChart: null,
      powerChart: null
    }
  },

  computed: {
    dateText() {
      const d = this.now
      const wk = ["星期日", "星期一", "星期二", "星期三", "星期四", "星期五", "星期六"]
      return d.getFullYear() + "年" + (d.getMonth() + 1) + "月" + d.getDate() + "日 " + wk[d.getDay()]
    },
    timeText() {
      return this.pad(this.now.getHours()) + ":" + this.pad(this.now.getMinutes()) + ":" + this.pad(this.now.getSeconds())
    },

    // 设备总数 = 在线 + 离线
    deviceTotal() {
      return this.device.online + this.device.offline
    },
    deviceOnlineRate() {
      const total = this.deviceTotal
      if (!total) return "0.0"
      return ((this.device.online / total) * 100).toFixed(1)
    },
    // 设备总览卡片（占比按设备总数计算）
    deviceList() {
      const total = this.deviceTotal
      const pct = (val) => {
        if (!total) return 0
        return Number(((val / total) * 100).toFixed(1))
      }
      return [
        //{ key: "online", label: "在线设备", value: this.device.online, icon: "el-icon-circle-check", pct: pct(this.device.online) },
        { key: "charging", label: "充电中", value: this.device.charging, icon: "el-icon-lightning", pct: pct(this.device.charging) },
        { key: "idle", label: "空闲中", value: this.device.idle, icon: "el-icon-time", pct: pct(this.device.idle) },
        { key: "fault", label: "故障设备", value: this.device.fault, icon: "el-icon-warning-outline", pct: pct(this.device.fault) },
        { key: "offline", label: "离线设备", value: this.device.offline, icon: "el-icon-remove-outline", pct: pct(this.device.offline) },
      ]
    },

    // 安全监测：监测总次数 = 正常 + 预警 + 报警
    safeTotal() {
      return this.safe.normal + this.safe.warn + this.safe.alarm
    },
    // 异常率 =（预警次数 + 报警次数）/（正常次数 + 预警次数 + 报警次数），按数值大小自动切换 %、‰、‱
    safeRateInfo() {
      return formatAbnormalRate(this.safe.warn, this.safe.alarm, this.safe.normal)
    },
    safeRate() {
      return this.safeRateInfo.value
    },
    safeRateUnit() {
      return this.safeRateInfo.unit
    },
    safeRateLevel() {
      const rate = this.safeRateInfo.percent
      if (rate >= 10) return "hi"
      if (rate >= 3) return "mid"
      return "ok"
    },
    // 内容超出卡片高度时复制一份数据，用于无缝滚动
    safeRenderList() {
      return this.safeScroll ? this.safe.list.concat(this.safe.list) : this.safe.list
    }
  },

  created() {
    document.title = "运营商数据大屏 - 统建统服系统"
  },

  mounted() {
    this.resizeScreen()
    window.addEventListener("resize", this.resizeScreen)

    if (screenfull.enabled) {
      screenfull.on("change", this.changeFullscreen)
    }

    // 点击空白处收起下拉
    document.addEventListener("click", this.closeSelect)

    this.timer = setInterval(() => {
      this.now = new Date()
    }, 1000)

    // 定时自动刷新（5 分钟，静默刷新不展示 loading）
    this.dataTimer = setInterval(() => {
      this.getPageData(false)
    }, 5 * 60 * 1000)

    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.initMonthChart()
        this.initPowerChart()
        this.getPageData()
      })
    })
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.resizeScreen)
    document.removeEventListener("click", this.closeSelect)
    if (screenfull.enabled) {
      screenfull.off("change", this.changeFullscreen)
    }
    if (this.timer) {
      clearInterval(this.timer)
    }
    if (this.dataTimer) {
      clearInterval(this.dataTimer)
    }
    if (this.monthChart) {
      this.monthChart.dispose()
      this.monthChart = null
    }
    if (this.powerChart) {
      this.powerChart.dispose()
      this.powerChart = null
    }
  },

  methods: {
    pad(n) {
      return n < 10 ? "0" + n : "" + n
    },
    // 千分位 + 两位小数
    formatReading(val) {
      if (val === null || val === undefined || val === "") return "0.00"
      const num = Number(val)
      if (isNaN(num)) return "0.00"
      return num.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },
    // 千分位整数
    formatInteger(val) {
      if (val === null || val === undefined || val === "") return "0"
      const num = Number(val)
      if (isNaN(num)) return "0"
      return String(Math.round(num)).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    },

    toggleSelect(key) {
      this.openSelect = this.openSelect === key ? "" : key
    },
    pickSelect(key, val) {
      if (key === "stat") this.statRange = val
      if (key === "rank") this.rankRange = val
      this.openSelect = ""
    },
    closeSelect() {
      this.openSelect = ""
    },

    // ======== 手动刷新 ========
    refreshData() {
      this.loading = true
      this.getPageData()
    },

    // ======== 全屏 ========
    toggleFullscreen() {
      if (!screenfull.enabled) {
        this.$message({ message: "当前浏览器不支持全屏", type: "warning" })
        return
      }
      screenfull.toggle(this.$el)
    },
    changeFullscreen() {
      this.isFullscreen = screenfull.isFullscreen
      this.resizeScreen()
      this.handleResize()
    },

    // ======== 大屏自适应缩放（1920x1080 等比居中，背景由外层铺满窗口） ========
    resizeScreen() {
      const el = this.$refs.screen
      if (!el) return
      const w = window.innerWidth
      const h = window.innerHeight
      const s = Math.min(w / 1920, h / 1080)
      el.style.transform = "translate(-50%, -50%) scale(" + s + ")"
      this.handleResize()
    },
    handleResize() {
      if (this.monthChart) this.monthChart.resize()
      if (this.powerChart) this.powerChart.resize()
      this.updateSafeScroll()
    },

    // ======== 数据请求 ========
    // 请求大屏全部数据；showLoading 为 false 时静默刷新（定时任务用）
    getPageData(showLoading = true) {
      if (showLoading) {
        this.loading = true
      }
      const done = () => {
        if (showLoading) {
          this.loading = false
        }
      }
      Promise.all([
        this.getHomeCurMonthBusiData(),
        this.getHomeUseElectChartData(),
        this.getHomeUseElectSiteYxPhData(),
        this.getHomeSafeMonitorData(),
        this.getHomeStrateData(),
        this.getHomeDeviceOverviewData()
      ]).then(done).catch(done)
    },

    // 设备总览：在线 / 离线 / 充电中 / 空闲 / 故障
    getHomeDeviceOverviewData() {
      // 接口仍为示例假路径，先用示例数据渲染；接口可用后把 deviceMock 置为 false
      if (this.deviceMock) {
        this.applyDeviceOverview(DEVICE_MOCK)
        return Promise.resolve()
      }
      return getHomeDeviceOverviewDataApi()
        .then((res) => {
          this.applyDeviceOverview((res && res.data) || res || {})
        })
        .catch((err) => {
          console.log("设备总览数据有误", err)
          this.applyDeviceOverview(DEVICE_MOCK)
        })
    },
    applyDeviceOverview(data) {
      const d = data || {}
      this.device = {
        online: Number(d.online_num) || 0,
        offline: Number(d.offline_num) || 0,
        charging: Number(d.charging_num) || 0,
        idle: Number(d.idle_num) || 0,
        fault: Number(d.fault_num) || 0
      }
    },

    // 首页本月运营数据
    getHomeCurMonthBusiData() {
      return getHomeCurMonthBusiDataApi()
        .then((res) => {
          const d = (res && res.data) || null
          if (!d) return
          this.statList = this.statList.map((item) => {
            const o = Object.assign({}, item)
            switch (item.key) {
              case "kwh":
                o.value = this.formatReading(d.month_elec / 1000)
                o.unit = "kWh"
                o.trend = d.month_elec_hb
                break
              case "fee":
                o.value = "¥" + this.formatReading(d.month_power_cost / 10000)
                o.unit = ""
                o.trend = d.month_power_cost_hb
                break
              case "svc":
                o.value = "¥" + this.formatReading(d.month_serv_cost / 10000)
                o.unit = ""
                o.trend = d.month_serv_cost_hb
                break
              case "ord":
                o.value = this.formatInteger(d.month_order_nums)
                o.unit = "单"
                o.trend = d.month_order_nums_hb
                break
              case "tim":
                o.value = this.formatReading(d.month_charge_time / 3600)
                o.unit = "小时"
                o.trend = d.month_charge_time_hb
                break
              case "new":
                o.value = this.formatInteger(d.month_init_num)
                o.unit = "户"
                o.trend = d.month_init_num_hb
                // 后端用 0.001 表示“新增”
                o.isNew = Number(d.month_init_num_hb) === 0.001
                break
              case "tot":
                o.value = this.formatInteger(d.all_init_num)
                o.unit = "户"
                o.note = "累计已开户"
                break
              case "left":
                o.value = this.formatInteger(d.wait_init_num)
                o.unit = "户"
                o.note = "可开户额度"
                break
            }
            if (!o.isNew && o.trend !== null && o.trend !== undefined && o.trend !== 0) {
              o.dir = Number(o.trend) > 0 ? "up" : "down"
            }
            return o
          })
        })
        .catch((err) => {
          console.log("首页运营数据有误", err)
        })
    },

    // 月度用电趋势
    getHomeUseElectChartData() {
      return getHomeUseElectChartDataApi()
        .then((res) => {
          const backData = (res && res.data) || []
          this.month = {
            labels: backData.map((item) => item.date_str),
            values: backData.map((item) => Number(((item.elect || 0) / 1000).toFixed(2)))
          }
          this.updateMonthChart()
        })
        .catch((err) => {
          console.log("月度用电趋势数据有误", err)
        })
    },

    // 站点用电排行
    getHomeUseElectSiteYxPhData() {
      return getHomeUseElectSiteYxPhDataApi()
        .then((res) => {
          const backData = (res && res.data) || []
          this.rankList = backData.slice(0, 8).map((item, idx) => ({
            id: item.power_site_id || idx + 1,
            name: item.power_site_name || "未命名站点",
            kwh: this.formatReading((item.elect_power || 0) / 1000),
            fee: this.formatReading((item.elect_fee || 0) / 10000),
            order: this.formatInteger(item.order_num),
            time: this.formatReading((item.all_charge_time || 0) / 3600)
          }))
        })
        .catch((err) => {
          console.log("站点用电排行数据有误", err)
        })
    },

    // 安全监测：正常 / 预警 / 报警次数 + 异常明细列表
    getHomeSafeMonitorData() {
      // 接口仍为示例假路径，先用示例数据渲染；接口可用后把 safeMock 置为 false
      if (this.safeMock) {
        this.applySafeMonitor(SAFETY_MONITOR_MOCK)
        return Promise.resolve()
      }
      return getHomeSafeMonitorDataApi()
        .then((res) => {
          this.applySafeMonitor((res && res.data) || res || {})
        })
        .catch((err) => {
          console.log("安全监测数据有误", err)
          this.applySafeMonitor(SAFETY_MONITOR_MOCK)
        })
    },
    applySafeMonitor(data) {
      const d = data || {}
      const list = Array.isArray(d.list) ? d.list : []
      this.safe = {
        normal: Number(d.normal_num) || 0,
        warn: Number(d.warn_num) || 0,
        alarm: Number(d.alarm_num) || 0,
        list: list.map((item, idx) => {
          // 列表按告警时间倒序展示
          const type = item.alarm_name || item.type_name || item.title || "未知异常"
          return {
            time: item.alarm_time || item.date_time || "--",
            type: type,
            color: this.getSafeTypeColor(type),
            // 后端：1 预警，2 报警（兼容 level / alarm_type 字段）
            level: Number(item.level || item.alarm_type) >= 2 ? 2 : 1,
            address: item.address || item.device_address || "--",
            key: item.id || idx
          }
        })
      }
      this.updateSafeScroll()
    },
    // 不同类型异常名称的颜色
    getSafeTypeColor(name) {
      if (!name) return "#8fa9c4"
      if (SAFE_TYPE_COLORS[name]) return SAFE_TYPE_COLORS[name]
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = (hash * 31 + name.charCodeAt(i)) % 9973
      }
      return SAFE_TYPE_PALETTE[hash % SAFE_TYPE_PALETTE.length]
    },
    // 列表超出卡片高度时开启无缝自动滚动
    updateSafeScroll() {
      this.$nextTick(() => {
        const el = this.$refs.safeList
        const list = this.safe.list
        if (!el || !list.length) {
          this.safeScroll = false
          return
        }
        const first = el.querySelector(".safe-item")
        const step = (first ? first.offsetHeight : 0) + SAFE_ITEM_GAP
        if (!step) {
          this.safeScroll = false
          return
        }
        const contentHeight = list.length * step
        this.safeScroll = contentHeight > el.clientHeight + 1
        // 约 26px/s 的匀速滚动
        this.safeDuration = Math.max(12, Math.round(contentHeight / 26))
      })
    },

    // 调控概览 + 24 小时功率曲线
    getHomeStrateData() {
      return getHomeStrateDataApi()
        .then((res) => {
          const d = (res && res.data) || {}
          this.power.ctrlCount = d.today_strate_num || 0
          this.power.ctrlPower = this.formatReading(d.today_strate_power)
          this.power.current = this.formatReading(d.cur_total_power)

          const chartData = d.chart_data || []
          const values = new Array(24).fill(null)
          chartData.forEach((item) => {
            const hh = this.parseHour(item.date_time)
            if (hh !== null && values[hh] === null) {
              values[hh] = Number(item.totalPower || 0)
            }
          })
          // 无数据点用前一个有效值补齐
          let last = 0
          values.forEach((val, idx) => {
            if (val === null) {
              values[idx] = last
            } else {
              last = val
            }
          })
          this.power.values = values
          this.updatePowerChart()
        })
        .catch((err) => {
          console.log("调控功率数据有误", err)
        })
    },

    parseHour(dateTime) {
      if (!dateTime) return null
      const text = String(dateTime)
      if (/^\d{1,2}$/.test(text)) {
        return Number(text) >= 0 && Number(text) <= 23 ? Number(text) : null
      }
      const matched = text.match(/\s(\d{1,2}):\d{1,2}/)
      if (matched) {
        const hh = Number(matched[1])
        return hh >= 0 && hh <= 23 ? hh : null
      }
      const matchedEnd = text.match(/(\d{1,2}):\d{1,2}/)
      if (matchedEnd) {
        const hh = Number(matchedEnd[1])
        return hh >= 0 && hh <= 23 ? hh : null
      }
      return null
    },

    // ---------- 用电月度统计 ----------
    initMonthChart() {
      const el = this.$refs.monthChart
      if (!el || this.monthChart) return
      const chart = echarts.init(el, null, { renderer: "canvas" })
      chart.setOption({
        grid: { left: 66, right: 26, top: 30, bottom: 32 },
        tooltip: Object.assign({}, TOOLTIP, {
          valueFormatter: function (v) {
            return Number(v).toLocaleString() + " kWh"
          }
        }),
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: this.month.labels,
          axisLine: { lineStyle: { color: C.axis } },
          axisTick: { show: false },
          axisLabel: { color: C.label, fontSize: 11.5, margin: 12 }
        },
        yAxis: {
          type: "value",
          splitLine: { lineStyle: { color: C.grid, type: "dashed" } },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: C.label,
            fontSize: 11.5,
            margin: 12,
            formatter: function (v) {
              return v >= 1000 ? v / 1000 + "k" : v
            }
          }
        },
        series: [
          {
            name: "用电量",
            type: "line",
            smooth: 0.36,
            symbol: "circle",
            symbolSize: 8,
            showSymbol: true,
            data: this.month.values,
            lineStyle: {
              width: 3,
              color: C.cyan,
              shadowColor: "rgba(0, 229, 255, .65)",
              shadowBlur: 16,
              shadowOffsetY: 4
            },
            itemStyle: {
              color: "#031a2b",
              borderColor: C.cyan,
              borderWidth: 2.5,
              shadowColor: "rgba(0, 229, 255, .9)",
              shadowBlur: 10
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "rgba(0, 229, 255, .42)" },
                { offset: 0.55, color: "rgba(0, 170, 255, .12)" },
                { offset: 1, color: "rgba(0, 170, 255, 0)" }
              ])
            },
            emphasis: {
              scale: 1.5,
              itemStyle: { color: C.cyan, borderColor: "#ffffff", borderWidth: 2 }
            }
          }
        ]
      })
      this.monthChart = chart
    },
    updateMonthChart() {
      if (!this.monthChart) return
      this.monthChart.setOption({
        xAxis: { data: this.month.labels },
        series: [{ name: "用电量", data: this.month.values }]
      })
    },

    // ---------- 功率曲线 ----------
    initPowerChart() {
      const el = this.$refs.powerChart
      if (!el || this.powerChart) return
      const chart = echarts.init(el, null, { renderer: "canvas" })
      chart.setOption({
        grid: { left: 56, right: 18, top: 20, bottom: 28 },
        tooltip: Object.assign({}, TOOLTIP, {
          axisPointer: { type: "line", lineStyle: { color: "rgba(0, 220, 255, .55)", type: "solid" } },
          formatter: function (params) {
            const p = params[0]
            return p.axisValue + ":00 &nbsp;&nbsp;" + p.data + " kW"
          }
        }),
        xAxis: {
          type: "category",
          boundaryGap: false,
          data: this.power.hours,
          axisLine: { lineStyle: { color: C.axis } },
          axisTick: { show: false },
          axisLabel: {
            color: C.label,
            fontSize: 10.5,
            margin: 10,
            interval: function (idx) {
              return idx % 3 === 0
            }
          }
        },
        yAxis: {
          type: "value",
          splitLine: { lineStyle: { color: C.grid, type: "dashed" } },
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: { color: C.label, fontSize: 10.5, formatter: "{value}" }
        },
        series: [
          {
            name: "功率",
            type: "line",
            step: "middle",
            symbol: "none",
            data: this.power.values,
            lineStyle: {
              width: 2.5,
              color: C.sky,
              shadowColor: "rgba(56, 198, 255, .7)",
              shadowBlur: 14
            },
            areaStyle: {
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: "rgba(56, 198, 255, .40)" },
                { offset: 0.6, color: "rgba(47, 140, 255, .10)" },
                { offset: 1, color: "rgba(47, 140, 255, 0)" }
              ])
            }
          }
        ]
      })
      this.powerChart = chart
    },
    updatePowerChart() {
      if (!this.powerChart) return
      const curHour = String(new Date().getHours())
      const curVal = this.power.values[Number(curHour)]
      const option = {
        xAxis: { data: this.power.hours },
        series: [{ name: "功率", data: this.power.values }]
      }
      if (curVal !== null && curVal !== undefined) {
        option.series[0].markPoint = {
          symbol: "circle",
          symbolSize: 9,
          data: [{ coord: [curHour, curVal] }],
          itemStyle: {
            color: C.cyan,
            borderColor: "#ffffff",
            borderWidth: 1.5,
            shadowColor: "rgba(0, 229, 255, 1)",
            shadowBlur: 12
          },
          label: { show: false }
        }
      }
      this.powerChart.setOption(option)
    }
  }
}
</script>

<style>
/* 大屏为独立展示页，禁用页面滚动，底色与大屏一致，避免出现白边 */
html,
body {
  overflow: hidden;
  background-color: #03091a;
}
</style>

<style lang="scss" scoped>
/* ===== 铺满整个浏览器窗口的背景层 ===== */
.screen-root {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  background:
    radial-gradient(1400px 700px at 50% -15%, rgba(0, 140, 255, 0.28), transparent 65%),
    radial-gradient(1000px 620px at 0% 100%, rgba(0, 220, 255, 0.1), transparent 70%),
    radial-gradient(1000px 620px at 100% 100%, rgba(80, 60, 255, 0.12), transparent 70%),
    linear-gradient(180deg, #03091a 0%, #041020 45%, #03080f 100%);
}

/* 背景网格 */
.screen-root::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(48, 130, 200, 0.055) 1px, transparent 1px),
    linear-gradient(90deg, rgba(48, 130, 200, 0.055) 1px, transparent 1px);
  background-size: 52px 52px;
  pointer-events: none;
  mask-image: radial-gradient(1200px 700px at 50% 40%, #000 20%, transparent 85%);
  -webkit-mask-image: radial-gradient(1200px 700px at 50% 40%, #000 20%, transparent 85%);
}

.screen {
  --c-cyan: #00e5ff;
  --c-blue: #2f8cff;
  --c-sky: #38c6ff;
  --c-green: #25e0a8;
  --c-yellow: #ffc53d;
  --c-red: #ff6b7d;

  --txt-1: #e8f4ff;
  --txt-2: #9fb6d0;
  --txt-3: #6d859f;

  --bd: rgba(45, 120, 190, 0.3);
  --bd-soft: rgba(48, 120, 190, 0.18);
  --bd-hi: rgba(0, 200, 255, 0.55);

  --panel-bg: linear-gradient(180deg, rgba(15, 45, 84, 0.55) 0%, rgba(6, 20, 38, 0.42) 100%);
  --card-bg: linear-gradient(145deg, rgba(20, 58, 102, 0.5), rgba(8, 24, 46, 0.34));

  --r-panel: 14px;
  --r-card: 11px;
  --r-sm: 8px;

  --gap: 16px;
  --ease: cubic-bezier(0.2, 0.8, 0.3, 1);

  position: absolute;
  left: 50%;
  top: 50%;
  width: 1920px;
  height: 1080px;
  transform-origin: center center;
  display: flex;
  flex-direction: column;
  padding: 14px 24px 20px;
  gap: 12px;
  overflow: hidden;
  font-family: "Microsoft YaHei", "PingFang SC", "Helvetica Neue", Arial, sans-serif;
  color: var(--txt-1);
}

.mono {
  font-family: "Bahnschrift", "DIN Alternate", "Segoe UI", Consolas, monospace;
  font-variant-numeric: tabular-nums;
}

.spin {
  animation: ds-rotate 1s linear infinite;
}
@keyframes ds-rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* ============ 顶部标题栏 ============ */
.hdr {
  position: relative;
  flex: 0 0 78px;
  height: 78px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.hdr::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 4px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(0, 190, 255, 0.55) 25%, rgba(0, 190, 255, 0.55) 75%, transparent);
}
.hdr::before {
  content: "";
  position: absolute;
  left: 50%;
  bottom: 4px;
  transform: translateX(-50%);
  width: 320px;
  height: 26px;
  background: radial-gradient(closest-side, rgba(0, 190, 255, 0.35), transparent);
  filter: blur(6px);
  pointer-events: none;
}
.hdr-title {
  font-size: 34px;
  font-weight: 700;
  letter-spacing: 7px;
  padding-left: 7px;
  margin-top: -9px;
  background: linear-gradient(180deg, #ffffff 18%, #8fdcff 88%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 20px rgba(0, 190, 255, 0.55));
  white-space: nowrap;
}
.hdr-sub {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 10px;
  font-size: 10px;
  letter-spacing: 4.5px;
  color: rgba(126, 196, 240, 0.55);
  white-space: nowrap;
}
.hdr-wing {
  position: absolute;
  top: 50%;
  width: 190px;
  height: 20px;
  transform: translateY(-50%);
  background: linear-gradient(90deg, transparent, rgba(0, 170, 255, 0.38));
  clip-path: polygon(0 45%, 76% 45%, 100% 0, 100% 100%, 76% 55%, 0 55%);
  pointer-events: none;
}
.hdr-wing.l {
  left: 340px;
}
.hdr-wing.r {
  right: 340px;
  transform: translateY(-50%) scaleX(-1);
}

.hdr-left,
.hdr-right {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 13px;
  color: var(--txt-2);
}
.hdr-left {
  left: 4px;
}
.hdr-right {
  right: 4px;
  gap: 12px;
}
.hdr-en {
  font-size: 12px;
  color: #5f7a95;
  letter-spacing: 1px;
}

.brand {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px 7px 11px;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(0, 120, 220, 0.26), rgba(0, 170, 255, 0.04));
  border: 1px solid rgba(0, 170, 255, 0.28);
}
.brand-ic {
  font-size: 17px;
  color: var(--c-sky);
  filter: drop-shadow(0 0 8px rgba(0, 200, 255, 0.8));
}
.brand b {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 1.2px;
  color: #d5ecff;
}

.hdr-btn {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  cursor: pointer;
  color: var(--c-sky);
  background: rgba(10, 34, 62, 0.6);
  border: 1px solid var(--bd-soft);
  transition: border-color 0.3s var(--ease), box-shadow 0.3s var(--ease), transform 0.3s var(--ease);
}
.hdr-btn:hover {
  border-color: var(--bd-hi);
  box-shadow: 0 0 16px rgba(0, 190, 255, 0.22);
  transform: translateY(-1px);
}
.hdr-btn i {
  font-size: 15px;
}

.hdr-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-radius: 999px;
  background: rgba(10, 34, 62, 0.6);
  border: 1px solid var(--bd-soft);
  transition: border-color 0.3s var(--ease), box-shadow 0.3s var(--ease);
}
.hdr-chip:hover {
  border-color: var(--bd-hi);
  box-shadow: 0 0 16px rgba(0, 190, 255, 0.22);
}
.chip-ic {
  font-size: 15px;
  color: var(--c-sky);
}
.chip-txt {
  font-size: 12.5px;
  color: var(--txt-2);
}
.time-val {
  font-size: 15px;
  color: #d9efff;
  letter-spacing: 0.8px;
}
.date-val {
  font-size: 13px;
  color: var(--txt-2);
}

.live-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--c-green);
  box-shadow: 0 0 10px var(--c-green);
  animation: pulse 1.8s ease-in-out infinite;
}
@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.35;
    transform: scale(0.72);
  }
}

/* ============ 主体：通栏设备总览 + 三列 ============ */
.main {
  flex: 1;
  min-height: 0;
  display: grid;
  grid-template-columns: 440px minmax(0, 1fr) 640px;
  grid-template-rows: auto minmax(0, 1fr);
  gap: var(--gap);
}
.col {
  display: flex;
  flex-direction: column;
  gap: var(--gap);
  min-height: 0;
  min-width: 0;
}
.col-l {
  grid-column: 1;
  grid-row: 2;
}
.col-c {
  grid-column: 2;
  grid-row: 2;
}
.col-r {
  grid-column: 3;
  grid-row: 2;
}

/* ============ 面板 ============ */
.panel {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 0 16px 16px;
  border-radius: var(--r-panel);
  background: var(--panel-bg);
  border: 1px solid var(--bd);
  box-shadow: inset 0 0 44px rgba(0, 132, 255, 0.07), 0 8px 26px rgba(0, 0, 0, 0.42);
  transition: border-color 0.35s var(--ease), box-shadow 0.35s var(--ease);
}
.panel:hover {
  border-color: var(--bd-hi);
  box-shadow: inset 0 0 44px rgba(0, 150, 255, 0.13), 0 10px 30px rgba(0, 0, 0, 0.5), 0 0 26px rgba(0, 180, 255, 0.16);
}
/* 科技角标 */
.panel::before,
.panel::after {
  content: "";
  position: absolute;
  width: 15px;
  height: 15px;
  border: 2px solid rgba(0, 200, 255, 0.75);
  pointer-events: none;
  transition: border-color 0.35s var(--ease);
}
.panel::before {
  left: -1px;
  top: -1px;
  border-right: 0;
  border-bottom: 0;
  border-radius: var(--r-panel) 0 0 0;
}
.panel::after {
  right: -1px;
  bottom: -1px;
  border-left: 0;
  border-top: 0;
  border-radius: 0 0 var(--r-panel) 0;
}
.panel:hover::before,
.panel:hover::after {
  border-color: rgba(0, 230, 255, 1);
}

.panel-hd {
  position: relative;
  flex: 0 0 auto;
  height: 52px;
  display: flex;
  align-items: center;
  gap: 9px;
}
.panel-hd::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 1px;
  background: linear-gradient(90deg, rgba(0, 200, 255, 0.75), rgba(0, 200, 255, 0.1) 55%, transparent);
}
.panel-hd .bar {
  width: 3px;
  height: 18px;
  border-radius: 2px;
  background: linear-gradient(180deg, var(--c-cyan), var(--c-blue));
  box-shadow: 0 0 10px rgba(0, 229, 255, 0.85);
}
.panel-hd .ic {
  font-size: 20px;
  color: var(--c-sky);
  filter: drop-shadow(0 0 8px rgba(0, 200, 255, 0.75));
}
.panel-hd h3 {
  font-size: 17px;
  font-weight: 600;
  letter-spacing: 1.6px;
  color: var(--txt-1);
}
.panel-hd .extra {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

.panel-bd {
  flex: 1;
  min-height: 0;
  padding-top: 14px;
  display: flex;
  flex-direction: column;
}

/* ============ 设备总览 ============ */
.dev-panel {
  grid-column: 1 / -1;
  grid-row: 1;
}
.dev-panel .panel-bd {
  padding-top: 12px;
}

.dv-hd-num {
  margin: 0 4px;
  font-size: 14.5px;
  font-weight: 700;
  color: var(--c-cyan);
  text-shadow: 0 0 14px rgba(0, 229, 255, 0.45);
}
.dv-hd-split {
  width: 1px;
  height: 12px;
  background: rgba(48, 120, 190, 0.45);
}

/* 卡片容器：内容超出时仅在本行内部滚动，不产生页面滚动条 */
.dev-cards {
  display: flex;
  gap: 14px;
  height: 64px;
  min-height: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 190, 255, 0.45) rgba(10, 32, 58, 0.55);

  &::-webkit-scrollbar {
    width: 5px;
    height: 5px;
  }
  &::-webkit-scrollbar-track {
    background-color: rgba(10, 32, 58, 0.55);
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 3px;
    background: linear-gradient(90deg, rgba(0, 229, 255, 0.5), rgba(47, 140, 255, 0.5));

    &:hover {
      background: linear-gradient(90deg, rgba(0, 229, 255, 0.85), rgba(47, 140, 255, 0.85));
    }
  }
  &::-webkit-scrollbar-corner {
    background-color: transparent;
  }
}

.dev-card {
  position: relative;
  overflow: hidden;
  flex: 1 1 0;
  min-width: 180px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 14px 0 18px;
  border-radius: var(--r-card);
  background: var(--card-bg);
  border: 1px solid rgba(48, 120, 190, 0.24);
  cursor: default;
  transition: border-color 0.3s var(--ease), box-shadow 0.3s var(--ease);
}
/* 左侧状态色竖条 */
.dev-card::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--dc), rgba(0, 0, 0, 0) 92%);
  box-shadow: 0 0 12px var(--dc);
}
/* 右上角状态色辉光 */
.dev-card::after {
  content: "";
  position: absolute;
  right: -26px;
  top: -34px;
  width: 128px;
  height: 128px;
  border-radius: 50%;
  background: radial-gradient(closest-side, var(--dc-soft), transparent 76%);
  pointer-events: none;
}
.dev-card:hover {
  border-color: var(--bd-hi);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.42), 0 0 20px var(--dc-soft);
}

.dev-card.online {
  --dc: var(--c-green);
  --dc-soft: rgba(37, 224, 168, 0.2);
}
.dev-card.offline {
  --dc: #7d94ad;
  --dc-soft: rgba(125, 148, 173, 0.18);
}
.dev-card.charging {
  --dc: var(--c-cyan);
  --dc-soft: rgba(0, 229, 255, 0.2);
}
.dev-card.idle {
  --dc: var(--c-sky);
  --dc-soft: rgba(56, 198, 255, 0.18);
}
.dev-card.fault {
  --dc: var(--c-red);
  --dc-soft: rgba(255, 107, 125, 0.2);
}

.dv-ic {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  border-radius: 11px;
  font-size: 21px;
  color: var(--dc);
  background: var(--dc-soft);
  box-shadow: inset 0 0 0 1px var(--dc-soft), 0 0 16px var(--dc-soft);
  transition: transform 0.32s var(--ease);
}
.dev-card:hover .dv-ic {
  transform: scale(1.08);
}

.dv-main {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-width: 0;
}
.dv-label {
  font-size: 12.5px;
  letter-spacing: 1px;
  color: var(--txt-2);
  white-space: nowrap;
}
.dv-val {
  display: flex;
  align-items: baseline;
  gap: 4px;
  margin-top: 2px;
}
.dv-val b {
  font-size: 23px;
  font-weight: 700;
  line-height: 1;
  color: var(--dc);
  text-shadow: 0 0 16px var(--dc-soft);
}
.dv-val small {
  font-size: 11.5px;
  color: var(--txt-3);
}

.dv-side {
  position: relative;
  z-index: 1;
  flex: 0 0 82px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 7px;
  margin-left: auto;
}
.dv-pct {
  font-size: 12.5px;
  letter-spacing: 0.5px;
  color: var(--txt-3);
}
.dv-bar {
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 2px;
  overflow: hidden;
  background: rgba(48, 120, 190, 0.24);
}
.dv-bar i {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 2px;
  background: linear-gradient(90deg, var(--dc), var(--dc-soft));
  box-shadow: 0 0 10px var(--dc);
  transition: width 0.7s var(--ease);
}

/* ============ 运营数据指标列表 ============ */
.stat-list {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.stat-row {
  position: relative;
  overflow: hidden;
  flex: 1 1 0;
  min-height: 0;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 0 15px;
  border-radius: var(--r-card);
  background: var(--card-bg);
  border: 1px solid rgba(48, 120, 190, 0.24);
  cursor: default;
  transition: transform 0.3s var(--ease), border-color 0.3s var(--ease), box-shadow 0.3s var(--ease);
}
/* 左侧高亮竖条 */
.stat-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  transform-origin: center;
  width: 3px;
  height: 58%;
  border-radius: 0 3px 3px 0;
  background: linear-gradient(180deg, var(--c-cyan), var(--c-blue));
  box-shadow: 0 0 12px rgba(0, 229, 255, 0.9);
  transition: transform 0.32s var(--ease);
}
/* 右侧辉光 */
.stat-row::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(200px 90px at 100% 50%, rgba(0, 210, 255, 0.18), transparent 72%);
  opacity: 0;
  transition: opacity 0.35s var(--ease);
  pointer-events: none;
}
.stat-row:hover {
  transform: translateX(5px);
  border-color: var(--bd-hi);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.45), 0 0 22px rgba(0, 190, 255, 0.2);
}
.stat-row:hover::before {
  transform: translateY(-50%) scaleY(1);
}
.stat-row:hover::after {
  opacity: 1;
}

.sr-ic {
  position: relative;
  z-index: 1;
  width: 48px;
  height: 48px;
  flex: 0 0 auto;
  display: grid;
  place-items: center;
  border-radius: 13px;
  font-size: 24px;
  transition: transform 0.32s var(--ease), box-shadow 0.32s var(--ease);
}
.stat-row:hover .sr-ic {
  transform: scale(1.08) rotate(-5deg);
  box-shadow: 0 0 18px rgba(0, 200, 255, 0.45);
}

.sr-main {
  position: relative;
  z-index: 1;
  flex: 1 1 auto;
  min-width: 0;
}
.sr-label {
  margin-bottom: 5px;
  font-size: 13.5px;
  color: var(--txt-2);
  letter-spacing: 0.5px;
}
.sr-val {
  display: flex;
  align-items: baseline;
  gap: 5px;
  min-width: 0;
}
.sr-val .num {
  font-size: 23px;
  font-weight: 700;
  color: #eaf7ff;
  letter-spacing: 0.3px;
  text-shadow: 0 0 18px rgba(0, 190, 255, 0.42);
  white-space: nowrap;
}
.sr-val .unit {
  font-size: 12px;
  color: #7f9ab5;
  white-space: nowrap;
}

.sr-side {
  position: relative;
  z-index: 1;
  flex: 0 0 94px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}
.sc-foot {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
}
.sc-foot i {
  font-size: 15px;
}
.sc-foot.up {
  color: var(--c-green);
  text-shadow: 0 0 14px rgba(37, 224, 168, 0.5);
}
.sc-foot.down {
  color: var(--c-red);
  text-shadow: 0 0 14px rgba(255, 107, 125, 0.5);
}
.sc-foot.flat {
  color: var(--txt-3);
}
.sc-foot.tag-new {
  width: fit-content;
  padding: 4px 9px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 500;
  color: var(--c-green);
  background: rgba(37, 224, 168, 0.12);
  border: 1px solid rgba(37, 224, 168, 0.32);
}
.sr-tip {
  font-size: 11.5px;
  color: var(--txt-3);
  letter-spacing: 0.5px;
}

/* ============ 自定义下拉 ============ */
.select {
  position: relative;
  user-select: none;
}
.select-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 10px 0 12px;
  border-radius: var(--r-sm);
  font-family: inherit;
  font-size: 12.5px;
  color: #cfe6fa;
  cursor: pointer;
  background: linear-gradient(180deg, rgba(22, 60, 104, 0.7), rgba(10, 30, 54, 0.7));
  border: 1px solid rgba(48, 120, 190, 0.35);
  transition: border-color 0.28s var(--ease), box-shadow 0.28s var(--ease), color 0.28s var(--ease);
}
.select-btn:hover {
  border-color: var(--bd-hi);
  color: #fff;
  box-shadow: 0 0 14px rgba(0, 190, 255, 0.22);
}
.select-btn .caret {
  font-size: 13px;
  color: var(--c-sky);
  transition: transform 0.3s var(--ease);
}
.select.open .select-btn {
  border-color: var(--bd-hi);
  box-shadow: 0 0 16px rgba(0, 190, 255, 0.28);
}
.select.open .select-btn .caret {
  transform: rotate(180deg);
}

.select-list {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  min-width: 100%;
  padding: 5px;
  margin: 0;
  z-index: 30;
  list-style: none;
  border-radius: 10px;
  background: linear-gradient(180deg, rgba(12, 38, 68, 0.98), rgba(6, 20, 38, 0.98));
  border: 1px solid rgba(0, 190, 255, 0.38);
  box-shadow: 0 14px 32px rgba(0, 0, 0, 0.6), 0 0 22px rgba(0, 180, 255, 0.18);
}
.select-list li {
  padding: 6px 12px;
  border-radius: 7px;
  font-size: 12.5px;
  color: #b9d3ea;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.22s var(--ease), color 0.22s var(--ease);
}
.select-list li:hover {
  background: rgba(0, 170, 255, 0.16);
  color: #fff;
}
.select-list li.active {
  background: rgba(0, 190, 255, 0.2);
  color: #6fe4ff;
}

.drop-enter-active,
.drop-leave-active {
  transition: opacity 0.2s var(--ease), transform 0.2s var(--ease);
}
.drop-enter,
.drop-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ============ 排行榜表格 ============ */
.rank-table {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  font-size: 13px;
}
.rt-head {
  display: flex;
  align-items: center;
  flex: 0 0 36px;
  font-size: 12.5px;
  color: var(--txt-3);
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(48, 120, 190, 0.22);
}
.rt-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-top: 4px;
}
.rt-row {
  display: flex;
  align-items: center;
  height: 41px;
  padding: 0 4px;
  border-radius: 9px;
  border-bottom: 1px solid rgba(48, 120, 190, 0.1);
  cursor: default;
  transition: background 0.28s var(--ease), transform 0.28s var(--ease), box-shadow 0.28s var(--ease);
}
.rt-row:hover {
  background: linear-gradient(90deg, rgba(0, 190, 255, 0.16), rgba(0, 190, 255, 0.02));
  transform: translateX(4px);
  box-shadow: inset 2px 0 0 var(--c-cyan);
}

.c-rank {
  flex: 0 0 46px;
  display: flex;
  justify-content: center;
}
.c-name {
  flex: 1 1 auto;
  min-width: 0;
  padding-right: 8px;
}
.c-kwh {
  flex: 0 0 92px;
  text-align: right;
}
.c-fee {
  flex: 0 0 84px;
  text-align: right;
}
.c-ord {
  flex: 0 0 62px;
  text-align: right;
}
.c-tim {
  flex: 0 0 88px;
  text-align: right;
}

.rk {
  display: grid;
  place-items: center;
  width: 23px;
  height: 23px;
  border-radius: 7px;
  font-size: 12px;
  font-weight: 700;
  font-family: "Bahnschrift", Consolas, monospace;
  background: rgba(80, 140, 200, 0.14);
  color: #a8c4dd;
  transition: transform 0.3s var(--ease);
}
.rt-row:hover .rk {
  transform: scale(1.12);
}
.rk.top1 {
  background: linear-gradient(135deg, #ffd75e, #ff9a2e);
  color: #3b2500;
  box-shadow: 0 0 14px rgba(255, 190, 60, 0.55);
}
.rk.top2 {
  background: linear-gradient(135deg, #e2edf9, #9fb8d4);
  color: #22303f;
  box-shadow: 0 0 14px rgba(200, 220, 255, 0.45);
}
.rk.top3 {
  background: linear-gradient(135deg, #ffbd85, #e0783c);
  color: #3a1d00;
  box-shadow: 0 0 14px rgba(255, 150, 80, 0.45);
}

.rn-1 {
  font-size: 13px;
  color: #e3f2ff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rn-2 {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 2px;
  font-size: 11px;
  color: var(--txt-3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rn-2 i {
  font-size: 11px;
  flex: 0 0 auto;
}

.v-kwh {
  font-size: 13.5px;
  font-weight: 600;
  color: var(--c-sky);
}
.v-fee {
  font-size: 13px;
  font-weight: 600;
  color: var(--c-green);
}
.v-ord {
  font-size: 13px;
  color: #c3d9ee;
}
.v-tim {
  font-size: 12.5px;
  color: #96b1c9;
}

.rt-empty {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12.5px;
  color: var(--txt-3);
}

/* ============ 安全监测 ============ */
/* 异常率 =（预警 + 报警）/（正常 + 预警 + 报警） */
.safe-rate {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 7px;
  font-size: 12.5px;
  color: var(--c-green);
  background: rgba(37, 224, 168, 0.12);
  border: 1px solid rgba(37, 224, 168, 0.32);
  transition: color 0.3s var(--ease), background 0.3s var(--ease), border-color 0.3s var(--ease);
}
.safe-rate i {
  font-size: 13px;
}
.safe-rate b {
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.5px;
}
.safe-rate.mid {
  color: var(--c-yellow);
  background: rgba(255, 197, 61, 0.12);
  border-color: rgba(255, 197, 61, 0.34);
}
.safe-rate.hi {
  color: var(--c-red);
  background: rgba(255, 107, 125, 0.12);
  border-color: rgba(255, 107, 125, 0.36);
}

/* 正常 / 预警 / 报警 三项统计 */
.safe-top {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  flex: 0 0 auto;
  margin-bottom: 12px;
}
.safe-box {
  position: relative;
  overflow: hidden;
  padding: 8px 6px 9px;
  border-radius: var(--r-card);
  text-align: center;
  transition: transform 0.3s var(--ease), box-shadow 0.3s var(--ease), border-color 0.3s var(--ease);
}
.safe-box::after {
  content: "";
  position: absolute;
  inset: 0;
  background: radial-gradient(110px 56px at 50% -20%, currentColor, transparent 72%);
  opacity: 0.16;
  pointer-events: none;
}
.safe-box:hover {
  transform: translateY(-3px);
}
.safe-box.normal {
  background: linear-gradient(160deg, rgba(37, 224, 168, 0.16), rgba(37, 224, 168, 0.03));
  border: 1px solid rgba(37, 224, 168, 0.32);
  color: var(--c-green);
}
.safe-box.normal:hover {
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.4), 0 0 20px rgba(37, 224, 168, 0.26);
  border-color: rgba(37, 224, 168, 0.68);
}
.safe-box.warn {
  background: linear-gradient(160deg, rgba(255, 197, 61, 0.16), rgba(255, 197, 61, 0.03));
  border: 1px solid rgba(255, 197, 61, 0.34);
  color: var(--c-yellow);
}
.safe-box.warn:hover {
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 197, 61, 0.28);
  border-color: rgba(255, 197, 61, 0.7);
}
.safe-box.alarm {
  background: linear-gradient(160deg, rgba(255, 107, 125, 0.16), rgba(255, 107, 125, 0.03));
  border: 1px solid rgba(255, 107, 125, 0.34);
  color: var(--c-red);
}
.safe-box.alarm:hover {
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.4), 0 0 20px rgba(255, 107, 125, 0.28);
  border-color: rgba(255, 107, 125, 0.7);
}
.sb-num {
  position: relative;
  z-index: 1;
  font-size: 22px;
  font-weight: 700;
  line-height: 1.15;
  white-space: nowrap;
}
/* 正常次数数值较长时（如 1,286,400）缩小字号，避免超出卡片 */
.sb-num-sm {
  font-size: 17px;
}
.safe-box.normal .sb-num {
  color: var(--c-green);
  text-shadow: 0 0 18px rgba(37, 224, 168, 0.5);
}
.safe-box.warn .sb-num {
  color: var(--c-yellow);
  text-shadow: 0 0 18px rgba(255, 197, 61, 0.5);
}
.safe-box.alarm .sb-num {
  color: var(--c-red);
  text-shadow: 0 0 18px rgba(255, 107, 125, 0.5);
}
.sb-lb {
  position: relative;
  z-index: 1;
  margin-top: 2px;
  font-size: 12px;
  color: #a7bdd4;
  letter-spacing: 0.5px;
}

/* 异常明细列表：撑满卡片剩余高度，内容超出后无缝自动滚动 */
.safe-list {
  position: relative;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}
.safe-track {
  will-change: transform;
}
.safe-track.run {
  animation: safe-marquee linear infinite;
}
.safe-track.run.paused {
  animation-play-state: paused;
}
@keyframes safe-marquee {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-50%);
  }
}

.safe-item {
  position: relative;
  margin-bottom: 8px; /* 需与脚本中 SAFE_ITEM_GAP 保持一致 */
  padding: 6px 12px 7px;
  border-radius: 9px;
  background: rgba(16, 44, 78, 0.42);
  border: 1px solid rgba(48, 120, 190, 0.16);
  border-left: 2px solid var(--lc, rgba(48, 120, 190, 0.42));
  transition: background 0.28s var(--ease), border-color 0.28s var(--ease);
}
.safe-item.lv1 {
  --lc: var(--c-yellow);
}
.safe-item.lv2 {
  --lc: var(--c-red);
}
.safe-item:hover {
  background: rgba(0, 140, 220, 0.17);
  border-color: rgba(0, 190, 255, 0.48);
}

.si-top {
  display: flex;
  align-items: center;
  gap: 10px;
}
.si-time {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  font-size: 12px;
  color: var(--txt-3);
}
.si-time i {
  font-size: 12px;
}
/* 异常名称最多展示 6 个中文宽度，超出省略号，hover 显示全称（title） */
.si-name {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 6em;
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;
  text-align: right;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: default;
}
.si-addr {
  display: flex;
  align-items: center;
  gap: 5px;
  margin-top: 3px;
  font-size: 11.5px;
  color: #8ba6bf;
}
.si-addr i {
  flex: 0 0 auto;
  font-size: 12px;
}
.si-addr span {
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

/* ============ 功率曲线 ============ */
.ctrl-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 40px;
  height: 40px;
  padding: 0 14px;
  margin-bottom: 10px;
  border-radius: 10px;
  background: rgba(16, 44, 78, 0.42);
  border: 1px solid rgba(48, 120, 190, 0.18);
  transition: border-color 0.3s var(--ease), box-shadow 0.3s var(--ease), background 0.3s var(--ease);
}
.ctrl-row:hover {
  border-color: rgba(0, 190, 255, 0.45);
  background: rgba(0, 120, 200, 0.14);
  box-shadow: 0 0 18px rgba(0, 180, 255, 0.18);
}
.ctrl-row .k {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  color: var(--txt-2);
}
.ctrl-row .k i {
  font-size: 15px;
  color: var(--c-sky);
}
.ctrl-row .v {
  font-size: 17px;
  font-weight: 700;
  color: var(--c-sky);
  text-shadow: 0 0 16px rgba(0, 190, 255, 0.5);
}
.ctrl-row .v small {
  font-size: 12px;
  font-weight: 400;
  color: #7f9ab5;
}

.chart-legend {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--txt-2);
}
.chart-legend i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--c-sky);
  box-shadow: 0 0 10px rgba(56, 198, 255, 0.85);
}
.chart-unit {
  font-size: 12px;
  color: var(--txt-3);
  letter-spacing: 0.6px;
}

.chart {
  flex: 1;
  min-height: 0;
  width: 100%;
}

/* 曲线面板内小标题 */
.sub-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: 0 0 auto;
  margin: 2px 0 4px;
  font-size: 12.5px;
  color: var(--txt-2);
}
.sub-line .cur {
  display: flex;
  align-items: baseline;
  gap: 5px;
}
.sub-line .cur b {
  font-size: 16px;
  color: var(--c-cyan);
  text-shadow: 0 0 14px rgba(0, 229, 255, 0.55);
}
.cur-unit {
  font-size: 12px;
  color: #7f9ab5;
}

/* 面板底部提示条 */
.foot-note {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
  font-size: 11.5px;
  color: var(--txt-3);
}
.foot-note i {
  font-size: 13px;
}
</style>